import { useRef, useState, useEffect } from "react";
import BiodataForm from "./BiodataForm";
import BiodataContainer from "./BiodataContainer";
import { defaultSections } from "../data";
import temp12 from "../assets/biodata/templates/temp-12.svg";

const STORAGE_KEY = "biodata_sections";
const TEMPLATE_STORAGE_KEY = "biodata_template";

const toBase64 = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };

    img.onerror = () =>
      reject("Template image cannot be loaded due to CORS");
  });

const DEFAULT_TEMPLATE = {
  id: 12,
  img: temp12,
  imgpre: temp12,
};

export default function Biodatamain() {
  const idRef = useRef(1);
  const getId = () => idRef.current++;

  // ================= SECTIONS =================
  const [sections, setSections] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {}
    }

    return defaultSections.map((s) => ({
      ...s,
      id: getId(),
      fields: s.fields.map((f) => ({ ...f, id: getId() })),
    }));
  });

  // ================= TEMPLATE =================
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(TEMPLATE_STORAGE_KEY);

    if (saved) {
      try {
        setTemplate(JSON.parse(saved));
        return;
      } catch {}
    }

    setTemplate(DEFAULT_TEMPLATE);
    localStorage.setItem(
      TEMPLATE_STORAGE_KEY,
      JSON.stringify(DEFAULT_TEMPLATE)
    );
  }, []);

  // ================= USER IMAGE =================
  const [userImage, setUserImage] = useState(null);

  useEffect(() => {
    const savedImage = localStorage.getItem("bdm_user_image");
    if (savedImage) setUserImage(savedImage);
  }, []);

  // ================= AUTO SAVE =================
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
  }, [sections]);

  // ================= TEMPLATE HANDLER =================
  const handleSelectTemplate = async (tpl) => {
    try {
      const base64 = tpl.img.startsWith("data:")
        ? tpl.img
        : await toBase64(tpl.img);

      const finalTemplate = { ...tpl, img: base64 };

      setTemplate(finalTemplate);
      localStorage.setItem(
        TEMPLATE_STORAGE_KEY,
        JSON.stringify(finalTemplate)
      );
    } catch {
      alert("Template cannot be loaded due to CORS issue");
    }
  };

  // ================= RESET =================
  const resetAllData = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TEMPLATE_STORAGE_KEY);
    localStorage.removeItem("bdm_user_image");

    idRef.current = 1;
    setSections(
      defaultSections.map((s) => ({
        ...s,
        id: idRef.current++,
        fields: s.fields.map((f) => ({
          ...f,
          id: idRef.current++,
        })),
      }))
    );

    setUserImage(null);

    setTemplate(DEFAULT_TEMPLATE);
    localStorage.setItem(
      TEMPLATE_STORAGE_KEY,
      JSON.stringify(DEFAULT_TEMPLATE)
    );
  };

  return (
    <>
      <div className="row">
        <div className="col form_sectbd">
          <BiodataForm
            sections={sections}
            setSections={setSections}
            userImage={userImage}
            setUserImage={setUserImage}
            onReset={resetAllData}
          />
        </div>

        <div className="col preview_sectbd">
          {template && (
            <BiodataContainer
              sections={sections}
              template={template}
              userImage={userImage}
              onTemplateChange={handleSelectTemplate}
            />
          )}
        </div>
      </div>
    </>
  );
}
